import React from 'react';
import { Badge, Dropdown, FormLabel } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store';

import { setParams } from '../reducers/paramsReducer';
import { BarLoader } from 'react-spinners';
import { analytics } from '../utils/analytics';

// Openings arrive whole, so the picker filters in the browser: every typed word must appear in the
// name, in any order ("caro exchange" finds "Caro Kann Defense Exchange Variation 3...cxd5").
function matches(name: string, terms: string[]): boolean {
  const haystack = name.toLowerCase();
  return terms.every((t) => haystack.includes(t));
}

export default function GamesFiltersOpenings(): React.ReactNode {
  const { opening } = useAppSelector((state) => state.paramsReducer.params);
  const { filters, loading } = useAppSelector((state) => state.filtersReducer);

  const dispatch = useAppDispatch();
  const [search, setSearch] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);

  const openings = filters?.Openings ?? [];
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = terms.length === 0 ? openings : openings.filter((o) => matches(o.Name, terms));

  const select = (name: string | null) => {
    dispatch(setParams({ opening: name }));
    if (name != null) {
      analytics.track('select_opening', { opening_name: name, ui_source: 'spa_filter' });
    }
  };

  return (
    <>
      <FormLabel>Openings</FormLabel>
      <Dropdown
        onToggle={(open) => {
          // Reset the search on open and focus the box.
          if (open) {
            setSearch('');
            window.setTimeout(() => searchRef.current?.focus(), 0);
          }
        }}
      >
        <Dropdown.Toggle className="cm-filter-select cm-openings-toggle">
          <span className="cm-openings-toggle-label">
            {opening != null ? opening : 'All openings'}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="cm-filter-menu cm-openings-menu">
          <div className="cm-openings-search" onClick={(e) => e.stopPropagation()}>
            <input
              ref={searchRef}
              type="text"
              className="cm-openings-search-input"
              placeholder="Search openings"
              aria-label="Search openings"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                // Enter picks the single remaining match.
                if (e.key === 'Enter' && shown.length === 1) {
                  select(shown[0].Name);
                }
              }}
            />
          </div>
          <div className="cm-openings-list">
            {loading ? (
              <Dropdown.Item disabled>
                <BarLoader color="#fff" />
              </Dropdown.Item>
            ) : null}
            {terms.length === 0 ? (
              <Dropdown.Item active={opening == null} onClick={() => select(null)}>
                All Openings
              </Dropdown.Item>
            ) : null}
            {opening != null && openings.find((d) => d.Name == opening) == null ? (
              <Dropdown.Item onClick={() => select(null)}>Opening: {opening}</Dropdown.Item>
            ) : null}
            {shown.map((op, i) => (
              <Dropdown.Item key={i} active={op.Name == opening} onClick={() => select(op.Name)}>
                <span className="cm-openings-name">{op.Name}</span>
                <Badge className="rounded-pill bg cm-openings-count">{op.GamesPlayed}</Badge>
              </Dropdown.Item>
            ))}
            {!loading && shown.length === 0 ? (
              <div className="cm-openings-empty">No openings match “{search.trim()}”</div>
            ) : null}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}
